import { getEventStart } from "@/lib/course-events";
import { courseScheduleAllowsBooking } from "@/lib/course-schedule";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import CallbackModal from "@/components/callback-modal";
import CourseBreadcrumb from "@/components/course-breadcrumb";
import Image from "@/components/image";
import NextImage from "next/image";
import LazyVideo from "@/components/lazy-video";
import { useSession } from "@/components/providers/session";
import { format } from "date-fns";
import { useState, Fragment } from "react";
import { resolveCourseSeatPriceComparison } from "@/utils/payment-plan";
import { currency } from "@/utils/formats";
import MD, { type Components } from "react-markdown";
import Link from "next/link";
import classNames from "@/utils/classnames";
import { documentToReactComponents } from "@/components/content/rich-text";
import { richContentOptions } from "@/components/content-options";
import {
  buildAdvisorCallbackPath,
  courseInterestFromPageCourse,
  recordCourseInterest,
} from "@/utils/course-interest";
import {
  REVIEWS_PAGE_PATH,
  formatGoogleBusinessProfileTrustStripItem,
  isGoogleReviewsTrustStripItem,
} from "@/lib/google-business-profile-reviews";
import { useGoogleBusinessProfileReviewStats } from "@/components/google-business-profile-review-stats-provider";
import { isVtctMoneyCoursePath, isVtctCoursePath } from "@/utils/vtct-course-page";
import { useRouter } from "next/router";

const VTCT_TRAINING_PROVIDER_URL =
  "https://www.vtctskills.org.uk/training-provider/cosmetic-college/";
const MODELS_PAGE_PATH = "/models/";
const FINANCE_PAGE_PATH = "/finance/";
const L5_TATTOO_PATH =
  "/courses/vtct/level-5-certificate-in-laser-tattoo-removal/";

const trustStripLinkClassName =
  "font-semibold text-brand-600 hover:text-brand-500";

function isVtctRegulatedTrustStripItem(item: string): boolean {
  return /^vtct-regulated$/i.test(item.trim());
}

function isModelsIncludedTrustStripItem(item: string): boolean {
  return /^models (included|supplied)$/i.test(item.trim());
}

function formatModelsTrustStripLabel(item: string): string {
  return /^models supplied$/i.test(item.trim())
    ? "Models supplied"
    : "Models included";
}

function isFinanceAvailableTrustStripItem(item: string): boolean {
  return /^0% (finance(?: available)?|in-house payment plans)$/i.test(
    item.trim(),
  );
}

function formatFinanceTrustStripLabel(item: string): string {
  if (/^0% in-house payment plans$/i.test(item.trim())) {
    return "0% in-house payment plans";
  }
  return /^0% finance available$/i.test(item.trim())
    ? "0% finance available"
    : item.trim();
}

const trustStripMdComponents: Components = {
  p: ({ children }) => <>{children}</>,
  strong: (props) => (
    <strong {...props} className="font-semibold text-brand-600" />
  ),
  a: ({ href, children }) =>
    href ? (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold text-brand-600 hover:text-brand-500"
      >
        {children}
      </Link>
    ) : (
      <span>{children}</span>
    ),
};

function TrustStripItem({ item }: { item: string }) {
  const stats = useGoogleBusinessProfileReviewStats();

  if (isGoogleReviewsTrustStripItem(item)) {
    return (
      <Link href={REVIEWS_PAGE_PATH} className={trustStripLinkClassName}>
        {formatGoogleBusinessProfileTrustStripItem(stats)}
      </Link>
    );
  }

  if (isVtctRegulatedTrustStripItem(item)) {
    return (
      <a
        href={VTCT_TRAINING_PROVIDER_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={trustStripLinkClassName}
      >
        VTCT-regulated
      </a>
    );
  }

  if (isModelsIncludedTrustStripItem(item)) {
    return (
      <Link href={MODELS_PAGE_PATH} className={trustStripLinkClassName}>
        {formatModelsTrustStripLabel(item)}
      </Link>
    );
  }

  if (isFinanceAvailableTrustStripItem(item)) {
    return (
      <Link href={FINANCE_PAGE_PATH} className={trustStripLinkClassName}>
        {formatFinanceTrustStripLabel(item)}
      </Link>
    );
  }

  return <MD components={trustStripMdComponents}>{item}</MD>;
}

function TrustStrip({ content }) {
  const items = content
    .split("·")
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (items.length <= 1) {
    return (
      <div className="flex mt-6 w-full justify-center lg:justify-start">
        <div className="relative inline-flex max-w-full items-center rounded-2xl px-4 py-2.5 text-center text-xs leading-5 text-gray-600 ring-1 ring-gray-900/10 shadow-lg shadow-brand-100 sm:rounded-full sm:py-1.5 sm:text-sm sm:leading-6 sm:text-left">
          <MD components={trustStripMdComponents}>{content}</MD>
        </div>
      </div>
    );
  }

  return (
    <div className="flex mt-6 w-full justify-center lg:justify-start">
      <div
        className={classNames(
          "w-full max-w-md rounded-2xl px-4 py-3 text-xs leading-5 text-gray-600 ring-1 ring-gray-900/10 shadow-lg shadow-brand-100 sm:w-auto sm:max-w-none sm:rounded-full sm:px-4 sm:py-1.5 sm:text-sm sm:leading-6",
          "grid grid-cols-2 gap-x-3 gap-y-2 sm:flex sm:items-center sm:gap-x-4"
        )}
      >
        {items.map((item, index) => (
          <Fragment key={index}>
            {index > 0 && (
              <span
                className="hidden h-4 w-px shrink-0 bg-gray-900/10 sm:block"
                aria-hidden="true"
              />
            )}
            <span
              className={classNames(
                "text-center whitespace-nowrap sm:text-left",
                items.length % 2 === 1 &&
                  index === items.length - 1 &&
                  "col-span-2"
              )}
            >
              <TrustStripItem item={item} />
            </span>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export default function Video({
  fields,
  first = false,
  sys,
  includes,
  primary,
  event,
  events,
}) {
  const session = useSession();
  const router = useRouter();
  const pathname = router.asPath.split("#")[0].split("?")[0];
  const normalizedPath = pathname.endsWith("/") ? pathname : `${pathname}/`;
  const isVtctMoneyCourse = isVtctMoneyCoursePath(normalizedPath);
  // Page-scoped: tattoo hero MP4 is ~1.9 MB; load only on click, keep poster look.
  const loadVideoOnClick = normalizedPath === L5_TATTOO_PATH;
  const titleParts = fields.title && fields.title.split(" ");
  const last = titleParts.pop();
  const Title = ({ children }) => {
    return first ? <h1>{children}</h1> : <h3>{children}</h3>;
  };
  const [state, setState] = useState({
    faqs: false,
    courses: false,
    showCallbackModal: false,
  });

  const nextCourse =
    event &&
    event.events &&
    event.events.length > 0 &&
    event.events.find((x) => courseScheduleAllowsBooking(x) && (x.countStudentRegistrations?.aggregate?.count ?? x.registrations?.length ?? 0) < x.seats);

  const courseInterest = courseInterestFromPageCourse({
    event,
    entry: primary,
    source: "advisor_cta",
  });
  const advisorCallbackHref = buildAdvisorCallbackPath(
    courseInterest,
    "course_page",
  );

  const startIso = getEventStart(nextCourse);
  const startDate = startIso ? new Date(startIso) : null;
  const nextCoursePricing = resolveCourseSeatPriceComparison(
    event,
    nextCourse?.seat_price ?? nextCourse?.price,
  );

  // L3
  const ytVideo =
    primary && primary.sys && primary.sys.id === "1ETJJG5eM8sxMLAzv3eWay"
      ? "https://www.youtube.com/embed/BDJZEfP5kkI?si=T_LOH70lj0IItTcU"
      : // L4
      primary && primary.sys && primary.sys.id === "1hxDZTDSW9Z4Ud4W0yVs5W"
      ? "https://www.youtube.com/embed/nOn5ivzuhPg?si=93Ivnib7EGYBBSi0"
      : // FDF
      primary && primary.sys && primary.sys.id === "2wzXw45yN0yJg4E6jPwvwv"
      ? "https://www.youtube.com/embed/Za-IH7V_j7A?si=rZSsq_9w0eyijKIx"
      : null;

  return (
    <div className="relative bg-white overflow-hidden">
      <div
        className="hidden lg:block lg:absolute lg:inset-0"
        aria-hidden="true"
      >
        <svg
          className="absolute top-0 left-1/2 transform translate-x-64 -translate-y-8"
          width="640"
          height="784"
          fill="none"
          viewBox="0 0 640 784"
        >
          <defs>
            <pattern
              id="9ebea6f4-a1f5-4d96-8c4e-4c2abf658047"
              x="118"
              y="0"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <rect
                x="0"
                y="0"
                width="4"
                height="4"
                className="text-gray-200"
                fill="currentColor"
              />
            </pattern>
          </defs>
          <rect
            y="72"
            width="640"
            height="640"
            className="text-gray-50"
            fill="currentColor"
          />
          <rect
            x="118"
            width="404"
            height="784"
            fill="url(#9ebea6f4-a1f5-4d96-8c4e-4c2abf658047)"
          />
        </svg>
      </div>
      <section className="mt-8 mb-12 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 lg:mt-16 lg:mb-16 relative z-10">
        <div className={
          classNames(
            "lg:gap-y-6 lg:flex lg:flex-col",
            ytVideo ? "" : "lg:grid lg:grid-cols-10 lg:gap-24"
          )
        }>
          <div
            className={classNames(
              "sm:text-center md:max-w-2xl md:mx-auto lg:text-left flex flex-col justify-center lg:mx-0",
              ytVideo ? "lg:col-span-5" : "lg:col-span-6"
            )}
          >
            {isVtctCoursePath(normalizedPath) ? (
              <CourseBreadcrumb
                pathname={normalizedPath}
                pageTitle={fields.title}
              />
            ) : null}
            {fields.logo ? (
              <div className="relative mt-4 h-8 w-40 md:mx-auto lg:mx-0">
                <NextImage
                  src={
                    fields.logo.fields.file.url.startsWith("//")
                      ? `https:${fields.logo.fields.file.url}`
                      : fields.logo.fields.file.url
                  }
                  className="object-contain object-left"
                  alt={fields.logo.fields.title || "VTCT"}
                  fill
                  sizes="160px"
                />
              </div>
            ) : null}
            {fields.title && (
              <Title>
                <span className="mt-1 block text-2xl md:text-4xl tracking-tight font-extrabold sm:text-5xl xl:text-6xl">
                  <span className="inline text-gray-900">
                    {titleParts.join(" ")}
                  </span>
                  {last && (
                    <span className="inline text-brand-600"> {last}</span>
                  )}
                </span>
              </Title>
            )}
            {nextCourse ? (
              <div className="flex mt-4 mb-2 md:mx-auto lg:mx-0">
                <div className="relative flex items-center gap-x-4 rounded-full px-4 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-brand-600 shadow-lg shadow-brand-100 transition-all duration-200 hover:shadow-lg hover:shadow-brand-200">
                  {nextCoursePricing.showCompare ? (
                    <span className="flex flex-col whitespace-nowrap tabular-nums leading-tight">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        Was {currency(nextCoursePricing.compareIncVatPence)}
                      </span>
                      <span className="font-semibold text-brand-600">
                        Now {currency(nextCoursePricing.currentIncVatPence)}
                      </span>
                    </span>
                  ) : (
                    <span className="font-semibold text-brand-600">
                      {currency(nextCoursePricing.currentIncVatPence)}
                    </span>
                  )}
                  <span
                    className="h-4 w-px bg-gray-900/10"
                    aria-hidden="true"
                  />
                  <a
                    href="#course-dates"
                    className="flex items-center gap-x-1 glow"
                  >
                    <span className="absolute inset-0" aria-hidden="true" />
                    Next course: {format(startDate, "do MMMM yyyy")}
                    <ChevronRightIcon
                      className="-mr-2 h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </a>
                </div>
              </div>
            ) : null}
            {ytVideo ? (
              <div className="relative block md:hidden mt-4 md:mt-0 h-full w-full rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 aspect-w-16 aspect-h-9">
                <iframe
                  width="560"
                  height="315"
                  src={ytVideo}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            ) : (
              <button
                type="button"
                className="relative block md:hidden mt-4 md:mt-0 w-full rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 aspect-w-16 aspect-h-9 bg-gray-100"
              >
                <span className="sr-only">Watch our video to learn more</span>
                {!fields.video && fields.image && (
                  <Image
                    alt=""
                    fill
                    priority
                    sizes="(min-width: 768px) 0px, 100vw"
                    fields={fields.image}
                    className="w-full h-full object-cover object-center"
                  />
                )}
                {fields.video && (
                  <LazyVideo
                    src={fields.video.fields.file.url}
                    poster={fields.image?.fields?.file?.url}
                    autoPlay
                    playsInline
                    muted
                    loop
                    controls
                    controlsList="nodownload nofullscreen noremoteplayback"
                    rootMargin="300px 0px"
                    loadOnClick={loadVideoOnClick}
                    className="absolute inset-0 h-full w-full object-cover object-center"
                  />
                )}
              </button>
            )}
            {fields.text && (
              <div className="pt-4 prose">
                <MD
                  components={{
                    strong: (props) => {
                      return <strong {...props} className="text-brand-600" />;
                    },
                  }}
                >
                  {fields.text}
                </MD>
              </div>
            )}
            {(fields.trustStrip || isVtctMoneyCourse) && (
              <TrustStrip
                content={
                  fields.trustStrip ||
                  "Google reviews · VTCT-regulated · Models included"
                }
              />
            )}
            {primary &&
              primary.fields.type &&
              primary.fields.type === "Course" && (
                <>
                  <div className="mt-6 flex flex-col md:flex-row items-center gap-y-4 md:gap-x-6 md:mx-auto lg:mx-0">
                    {nextCourse ? (
                    <button
                      type="button"
                      onClick={() => {
                        session.addCourse({
                          ...primary,
                          event,
                          package: event && event.package,
                          selectedEvent: nextCourse
                            ? nextCourse
                            : events && events.length > 0 && events[0]
                            ? events[0]
                            : null,
                        });
                        setTimeout(() => {
                          window.location.href = "/checkout";
                        }, 100);
                      }}
                      className="w-full md:w-max rounded-md bg-brand-600 px-6 py-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500"
                    >
                      <span className="relative z-[1]">Book Your Training</span>
                    </button>
                    ) : (
                      <a
                        href="#pricing"
                        className="w-full md:w-max rounded-md bg-brand-600 px-6 py-4 text-sm font-semibold text-white text-center shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500"
                      >
                        View fees and arrange training
                      </a>
                    )}
                    <Link
                      href={advisorCallbackHref}
                      onClick={() => {
                        if (courseInterest) {
                          recordCourseInterest({
                            ...courseInterest,
                            source: "advisor_cta",
                            captured_at: new Date().toISOString(),
                          });
                        }
                      }}
                      className="w-full md:w-max group rounded-md text-sm font-semibold px-6 py-4 leading-6 eading-6 text-gray-600 ring-2 ring-gray-900/10 hover:ring-brand-600 shadow-lg shadow-brand-100 transition-all duration-200 hover:shadow-lg hover:shadow-brand-200 text-center"
                    >
                      Speak With An Advisor{" "}
                      <span
                        aria-hidden="true"
                        className="group-hover:pl-1 transition-all duration-150"
                      >
                        →
                      </span>
                    </Link>
                    <a
                      href="https://calendar.app.google/DNfJkzqESkA3NSRM7"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full md:w-max group rounded-md text-sm font-semibold px-6 py-4 leading-6 eading-6 text-gray-600 ring-2 ring-gray-900/10 hover:ring-brand-600 shadow-lg shadow-brand-100 transition-all duration-200 hover:shadow-lg hover:shadow-brand-200 text-center"
                    >
                      <span className="relative z-[1] text-center">
                        Book A College Tour
                      </span>
                    </a>
                    {/* <button
                      type="button"
                      onClick={() => {
                        setState({ ...state, showCallbackModal: true });
                      }}
                      className="w-full md:w-max group rounded-md text-sm font-semibold px-6 py-4 leading-6 eading-6 text-gray-600 ring-2 ring-gray-900/10 hover:ring-brand-600 shadow-lg shadow-brand-100 transition-all duration-200 hover:shadow-lg hover:shadow-brand-200"
                    >
                      Speak with an advisor{" "}
                      <span
                        aria-hidden="true"
                        className="group-hover:pl-1 transition-all duration-150"
                      >
                        →
                      </span>
                    </button> */}
                  </div>
                </>
              )}
            {fields.additional && (
              <div className="mt-8 prose">
                {documentToReactComponents(
                  fields.additional,
                  richContentOptions
                )}
              </div>
            )}
          </div>
          <div
            className={classNames(
              "hidden md:block mt-12 relative sm:max-w-lg sm:mx-auto lg:mt-0 lg:max-w-none lg:mx-0 lg:flex",
              ytVideo ? "lg:col-span-5 lg:items-start" : "lg:col-span-4 lg:items-center"
            )}
          >
            <svg
              className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-8 scale-75 origin-top sm:scale-100 lg:hidden"
              width="640"
              height="784"
              fill="none"
              viewBox="0 0 640 784"
              aria-hidden="true"
            >
              <defs>
                <pattern
                  id="4f4f415c-a0e9-44c2-9601-6ded5a34a13e"
                  x="118"
                  y="0"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x="0"
                    y="0"
                    width="4"
                    height="4"
                    className="text-gray-200"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                y="72"
                width="640"
                height="640"
                className="text-gray-50"
                fill="currentColor"
              />
              <rect
                x="118"
                width="404"
                height="784"
                fill="url(#4f4f415c-a0e9-44c2-9601-6ded5a34a13e)"
              />
            </svg>
            <div className="relative w-full rounded-lg shadow-lg">
              {ytVideo ? (
                <div className="relative block mt-0 md:mt-0 h-full w-full rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 aspect-w-16 aspect-h-9">
                  <iframe
                    src={ytVideo}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              ) : (
                <button
                  type="button"
                  className={`relative block w-full rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 bg-gray-100 ${
                    !fields.video && fields.image
                      ? "min-h-[450px]"
                      : "aspect-w-16 aspect-h-9"
                  }`}
                >
                  <span className="sr-only">Watch our video to learn more</span>
                  {!fields.video && fields.image && (
                    <Image
                      alt=""
                      fill
                      priority
                      sizes="(min-width: 1024px) 40vw, (min-width: 768px) 50vw, 0px"
                      fields={fields.image}
                      className="w-full h-full object-cover object-center"
                    />
                  )}
                  {fields.video && (
                    <LazyVideo
                      src={fields.video.fields.file.url}
                      poster={fields.image?.fields?.file?.url}
                      autoPlay
                      playsInline
                      muted
                      loop
                      controls
                      controlsList="nodownload nofullscreen noremoteplayback"
                      rootMargin="300px 0px"
                      loadOnClick={loadVideoOnClick}
                      className="absolute inset-0 h-full w-full object-cover object-center"
                    />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
      {state.showCallbackModal && (
        <CallbackModal
          event={event}
          showCallbackModal={state.showCallbackModal}
          setShow={setState}
        />
      )}
    </div>
  );
}
